import React, { useEffect, useMemo, useState } from 'react';
import CommonCard from '../../../global/card';
import CommonSkeletonLoader from '../../../global/loading';
import { useLocation } from 'react-router-dom';
import SOURCES from '../../../../utils/constants';
import { useGenericQuery } from '../../../../queries/use-generic-query';
import { QUERY_KEYS } from '../../../../lib/query-constants';
import useDebounce from '../../../../utils/customHooks';
import CommonPagination from '../../../global/pagination';
import EnhancedActivityLogList from './EnhancedActivityLogList';

const EnhancedActivityLog = () => {
    const { DEFAULT_RECORDS_LISTING, DEFAULT_RECORDS_SEARCH } = SOURCES;

    const [searchActivityLog, setSearchActivityLog] = useState("");
    const [activityLogViewType, setActivityLogViewType] = useState(() => {
        return localStorage.getItem("activityLogViewType") || "GRID";
    });
    const [sort, setSort] = useState({ sortType: "dateAsPerUserTimezone", sortOrder: -1 });

    // Pagination state
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const pageFromQuery = parseInt(searchParams.get("page") || 1, 10);
    const limitFromQuery = parseInt(searchParams.get("limit") || DEFAULT_RECORDS_LISTING, 10);
    const offsetFromQuery = parseInt(searchParams.get("offset") || "0", 10);

    const [pagination, setPagination] = useState({
        offset: offsetFromQuery,
        limit: limitFromQuery,
        page: pageFromQuery,
    });

    const [paginationSearch, setPaginationSearch] = useState({
        offsetSearch: 0,
        limitSearch: DEFAULT_RECORDS_SEARCH,
    });

    const debouncedSearchTerm = useDebounce(searchActivityLog, 500);

    // Params for main listing
    const keyParamsForActivityLog = {
        offset: pagination.offset,
        limit: pagination.limit,
        sortBy: sort.sortType,
        sortOrder: sort.sortOrder,
    };

    // Params for search
    const searchKeyParams = {
        offset: paginationSearch.offsetSearch,
        limit: paginationSearch.limitSearch,
        q: debouncedSearchTerm,
    };

    // Main Data Query
    // Map to 'reports/userlog'
    const { data: activityLogData, isLoading: activityLogLoading, isFetching: activityLogFetching } = useGenericQuery({
        endpoint: "reports/userlog",
        queryKey: QUERY_KEYS.activityLog.lists({ ...keyParamsForActivityLog }),
        queryParams: { ...keyParamsForActivityLog },
        enabled: !debouncedSearchTerm
    });

    // Search Data Query
    // Map to 'reports/userlog/search'
    const { data: searchData, isInitialLoading: searchDataLoading } = useGenericQuery({
        endpoint: "reports/userlog/search",
        queryKey: QUERY_KEYS.activityLog.lists({ ...searchKeyParams }),
        queryParams: { ...searchKeyParams },
        enabled: !!debouncedSearchTerm.trim()
    });

    // Determine final data
    const getFinalData = useMemo(() => {
        if (debouncedSearchTerm) {
            return searchData?.results || [];
        }
        return activityLogData?.results || [];
    }, [activityLogData, searchData, debouncedSearchTerm]);

    const totalRecords = debouncedSearchTerm ? (searchData?.totalRecords || 0) : (activityLogData?.totalRecords || 0);
    const isLoading = activityLogLoading || searchDataLoading;
    const dataExist = getFinalData && getFinalData.length > 0;

    return (
        <CommonCard
            isSearch={{
                show: true,
                placeholder: "Search activity log...",
                searchInput: searchActivityLog,
                setSearchInput: setSearchActivityLog
            }}
            isListGridView={{
                show: true,
                viewType: activityLogViewType,
                setViewType: setActivityLogViewType,
                viewFor: "activityLogViewType"
            }}
            isLoading={{
                show: isLoading,
                loaderCmp: (
                    <CommonSkeletonLoader
                        type={activityLogViewType === "GRID" ? "card" : "table"}
                    />
                ),
            }}
            className={(!isLoading && activityLogFetching) ? "opacity-50" : ""}
        >
            <EnhancedActivityLogList
                isLoading={isLoading}
                activityLogData={getFinalData}
                sort={sort}
                setSort={setSort}
                viewType={activityLogViewType}
            />

            {(dataExist && !searchActivityLog) &&
                totalRecords > DEFAULT_RECORDS_LISTING && (
                    <CommonPagination
                        currentPage={pagination.page}
                        defaultLimit={pagination.limit}
                        totalRecords={totalRecords}
                        setCurrentPage={setPagination}
                        activePageRecords={getFinalData?.length || 0}
                        paginationFor="Activity Log"
                        className="mt-4"
                    />
                )}
        </CommonCard>
    );
};

export default EnhancedActivityLog;